import { GraphUtils } from '@helpers/graph.helper';
import { Router } from '@angular/router';
import { Sensor } from '@models/sensor/sensor';
import { UtilsHelper } from '@helpers/utils.helper';

export class WaterCounterPulseAttribute extends Sensor {
  public waterGraph: any;
  public waterHourGraph: any;
  public waterMonthGraph: any;
  public iconsToDisplay: { icon: string, type: number }[] = [];

  private waterSensor: any[][] = [];
  private waterHourSensor: any[][] = [];
  private waterMonthSensor: any[][] = [];
  private lastWaterRegister: any[] = []; // index = position, value = register
  private sensorProperties: Map<string, any> = new Map();
  private sensorIdByPosition: Map<number, string> = new Map();
  private equivalenceByPosition: number[] = [];

  constructor(router: Router, url: string, alertsActive: boolean) {
    super(router, url, alertsActive);
    this.waterGraph = null;
  }

  // Método para recibir propiedades de sensores y mapear por sensorId
  public setSensorProperties(properties: Map<string, any>, sensors?: any[]): void {
    this.sensorProperties = properties;

    // Crear y guardar mapa de position → sensorId para poder encontrarlo aunque no haya datos en /data
    this.sensorIdByPosition.clear();
    if (sensors && sensors.length > 0) {
      sensors.forEach(sensor => {
        this.sensorIdByPosition.set(sensor.position, sensor.id);
      });
    }
  }

  private ensureArraySize1D(arr: any[], position: number): void {
    while (arr.length <= position) arr.push(null);
  }


  private ensureArraySize(arr: any[][], position: number): void {
    while (arr.length <= position) {
      arr.push([]);
    }
  }

  public hasWaterSensor(pos: number): boolean {
    const index = pos - 1;
    return (
      index >= 0 &&
      index < this.waterSensor.length &&
      this.waterSensor[index].length > 0
    );
  }

  public getWaterSensor(pos: number): any {
    const index = pos - 1;
    if (index >= 0 && index < this.waterSensor.length) {
      const sensorData = this.waterSensor[index];
      if (sensorData.length > 0) {
        return sensorData[sensorData.length - 1][1] ?? null;
      }
    }
    return null;
  }

  public hasHourWaterSensor(pos: number): boolean {
    const index = pos - 1;
    return (
      index >= 0 &&
      index < this.waterHourSensor.length &&
      this.waterHourSensor[index].length > 0
    );
  }

  public getHourWaterSensor(pos: number): any {
    const index = pos - 1;
    if (index >= 0 && index < this.waterHourSensor.length) {
      const sensorData = this.waterHourSensor[index];
      if (sensorData.length > 0) {
        return sensorData[sensorData.length - 1][1] ?? null;
      }
    }
    return null;
  }

  public hasMonthWaterSensor(pos: number): boolean {
    const index = pos - 1;
    return (
      index >= 0 &&
      index < this.waterMonthSensor.length &&
      this.waterMonthSensor[index].length > 0
    );
  }

  public getMonthWaterSensor(pos: number): any {
    const index = pos - 1;
    if (index >= 0 && index < this.waterMonthSensor.length) {
      const sensorData = this.waterMonthSensor[index];
      if (sensorData.length > 0) {
        return sensorData[sensorData.length - 1][1] ?? null;
      }
    }
    return null;
  }

  public getDisplayWaterValue(value: number, pos: number): number {
    const equivalence = this.getEquivalence(pos);

    if (value === null || value === undefined) {
      return value;
    }

    return equivalence > 0 && equivalence < 1 ? value * 1000 : value;
  }

  public getDisplayWaterUnit(pos: number): string {
    const equivalence = this.getEquivalence(pos);

    return equivalence > 0 && equivalence < 1 ? 'L' : 'm3';
  }

  private getEquivalence(pos: number): number {
    return this.equivalenceByPosition[pos - 1] ?? 1;
  }

  public prepareData(): void {
    const iconMap = new Map<string, { icon: string, type: number }>();
    const lastDateByPosition = new Map<number, string>();

    for (const element of this.data) {
      const pos = element.position ?? 0;
      const currentLastDate = lastDateByPosition.get(pos);

      if (!currentLastDate || new Date(element.date).getTime() >= new Date(currentLastDate).getTime()) {
        lastDateByPosition.set(pos, element.date);
      }
    }

    this.lastWaterRegister = [];

    for (const element of this.data) {
      const pos = element.position ?? 0;
      const liters = element.water ?? 0;
      const equivalence = Number(element.equivalence);
      const sensorInfo = UtilsHelper.nameSensor(element.type);
      const icon = sensorInfo.icon;

      if (!iconMap.has(icon)) {
        iconMap.set(icon, {
          icon,
          type: element.type
        });
      }

      // SOLO usar sensor_properties como fuente de verdad
      let sensorId = element.sensorId;

      // Si no tiene sensorId, intentar obtenerlo por position
      if (!sensorId && this.sensorIdByPosition.has(element.position)) {
        sensorId = this.sensorIdByPosition.get(element.position);
      }

      let register = sensorId ? this.sensorProperties.get(sensorId)?.waterRegister : undefined;

      this.ensureArraySize1D(this.lastWaterRegister, pos);

      if (element.date === lastDateByPosition.get(pos)) {
        this.lastWaterRegister[pos] = null;
      }

      if (element.date === lastDateByPosition.get(pos) && register !== null && register !== undefined) {
        this.lastWaterRegister[pos] = register;
      }

      if (Number.isFinite(equivalence)) {
        this.equivalenceByPosition[pos] = equivalence;
      }


      this.ensureArraySize(this.waterSensor, pos);
      this.ensureArraySize(this.waterHourSensor, pos);
      this.ensureArraySize(this.waterMonthSensor, pos);

      const date = new Date(element.date);
      const day = GraphUtils.getDay(date);
      const hour = GraphUtils.getHour(date);
      const month = GraphUtils.getMoth(date);

      // Día
      const currentDay = this.waterSensor[pos].find(e => e[0] === day);
      if (currentDay) {
        currentDay[1] += liters;
      } else {
        this.waterSensor[pos].push([day, liters]);
      }

      // Hora
      const currentHour = this.waterHourSensor[pos].find(e => e[0] === hour);
      if (currentHour) {
        currentHour[1] += liters;
      } else {
        this.waterHourSensor[pos].push([hour, liters]);
      }

      // Mes
      const currentMonth = this.waterMonthSensor[pos].find(e => e[0] === month);
      if (currentMonth) {
        currentMonth[1] += liters;
      } else {
        this.waterMonthSensor[pos].push([month, liters]);
      }
    }
    this.iconsToDisplay = Array.from(iconMap.values());

  }


  public hasWaterRegister(pos: number): boolean {
    const index = pos - 1;
    return index >= 0 && index < this.lastWaterRegister.length && this.lastWaterRegister[index] !== undefined && this.lastWaterRegister[index] !== null;
  }

  public getWaterRegister(pos: number): any {
    const index = pos - 1;
    return index >= 0 && index < this.lastWaterRegister.length ? this.lastWaterRegister[index] : null;
  }


  public setCharts(): void {
    const filteredWaterSensor = this.waterSensor.filter(series => series.length > 0);
    const positionNodeWaterSensor = this.waterSensor
      .map((item, index) => (item.length > 0 ? index + 1 : -1))
      .filter(index => index !== -1);

    this.waterGraph = this.getConfigChar(
      'pulse',
      this.getChartSeriesNewNode('Sensor', filteredWaterSensor, positionNodeWaterSensor),
      this.getRanges('43_value_daily'),
      this.getLimits('43_value_daily'),
      'column'
    );

    const filteredWaterHour = this.waterHourSensor.filter(series => series.length > 0);
    const positionNodeWaterHour = this.waterHourSensor
      .map((item, index) => (item.length > 0 ? index + 1 : -1))
      .filter(index => index !== -1);

    this.waterHourGraph = this.getConfigChar(
      'pulseHour',
      this.getChartSeriesNewNode('Sensor', filteredWaterHour, positionNodeWaterHour),
      this.getRanges('43_value_hour'),
      this.getLimits('43_value_hour'),
      'column'
    );

    const filteredWaterMonth = this.waterMonthSensor.filter(series => series.length > 0);
    const positionNodeWaterMonth = this.waterMonthSensor
      .map((item, index) => (item.length > 0 ? index + 1 : -1))
      .filter(index => index !== -1);

    this.waterMonthGraph = this.getConfigChar(
      'pulseMonth',
      this.getChartSeriesNewNode('Sensor', filteredWaterMonth, positionNodeWaterMonth),
      this.getRanges('43_value_monthly'),
      this.getLimits('43_value_monthly'),
      'column'
    );
  }

  public static getVariables(): any[] {
    return [
      { title: 'Consumo de agua: por hora', value: '43_value_hour' },
      { title: 'Consumo de agua: por día', value: '43_value_daily' },
      { title: 'Consumo de agua: por mes', value: '43_value_monthly' }
    ];
  }


}
